<template>
  <!-- How to connect the Prefetch MCP server, one tab per AI client — the
       agent half of prefetch.io's quickstart window, in Colorize's colours.
       Emits `engage` on any deliberate use (a tab, a copy) so the rotating ad
       around it stops moving the snippet out from under the reader. -->
  <CodeWindow
    :html="highlighted"
    :lines="10"
    :body-attrs="{ id: panelId, role: 'tabpanel', 'aria-labelledby': tabId(current.id), tabindex: 0 }"
  >
    <template #header>
      <div
        role="tablist"
        aria-label="AI client"
        class="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none]"
        @keydown="onKeydown"
      >
        <button
          v-for="client in mcpClients"
          :id="tabId(client.id)"
          :key="client.id"
          type="button"
          role="tab"
          :aria-selected="client.id === current.id"
          :aria-controls="panelId"
          :tabindex="client.id === current.id ? 0 : -1"
          class="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold transition focus-visible:-outline-offset-2"
          :class="client.id === current.id ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'"
          @click="select(client)"
        >
          <svg viewBox="0 0 24 24" class="mcp-snippet__glyph h-3.5 w-3.5 shrink-0" fill="currentColor" aria-hidden="true">
            <path
              :d="client.glyph.d"
              :transform="client.glyph.scale === 1 ? undefined : `translate(12 12) scale(${client.glyph.scale}) translate(-12 -12)`"
            />
          </svg>
          {{ client.label }}
        </button>
      </div>

      <button
        type="button"
        class="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-gray-400 transition hover:bg-white/5 hover:text-gray-200 focus-visible:-outline-offset-2"
        @click="copy"
      >
        <CheckIcon v-if="copied" class="h-4 w-4 text-emerald-400" aria-hidden="true" />
        <DocumentDuplicateIcon v-else class="h-4 w-4" aria-hidden="true" />
        <span class="mcp-snippet__copy-label" aria-live="polite">{{ copied ? 'Copied' : 'Copy' }}</span>
      </button>
    </template>
  </CodeWindow>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, useId } from 'vue'
import { CheckIcon, DocumentDuplicateIcon } from '@heroicons/vue/16/solid'
import CodeWindow from '~/components/atoms/CodeWindow.vue'
import { trackEvent } from '~/composables/analytics'
import { highlightCode } from '~/utils/highlightCode'
import { mcpClients } from '~/utils/mcpSetup'

const emit = defineEmits(['engage'])

// SSR-stable, so the tab/panel wiring survives hydration.
const uid = useId()
const panelId = `${uid}-panel`
const tabId = (id) => `${uid}-tab-${id}`

const current = ref(mcpClients[0])
const highlighted = computed(() => highlightCode(current.value.snippet, current.value.language))

function select(client) {
  emit('engage')
  if (client.id === current.value.id) return
  current.value = client
  // The confirmation belongs to the snippet that was copied, not to whatever
  // is on screen now.
  copied.value = false
  try {
    trackEvent(`MCP Snippet Tab (${client.id})`)
  } catch {}
}

// Arrow keys move between tabs, as the WAI-ARIA tabs pattern expects; the
// strip is one tab stop (roving tabindex above).
function onKeydown(event) {
  const i = mcpClients.findIndex((c) => c.id === current.value.id)
  const last = mcpClients.length - 1
  const to = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last }[event.key]
  if (to === undefined) return
  event.preventDefault()
  select(mcpClients[to])
  document.getElementById(tabId(mcpClients[to].id))?.focus()
}

const copied = ref(false)
let copyTimer = null

async function copy() {
  emit('engage')
  try {
    await navigator.clipboard.writeText(current.value.snippet)
  } catch {
    // Clipboard access can be denied (insecure context, permissions); leaving
    // the label alone is a truthful "nothing was copied".
    return
  }
  copied.value = true
  try {
    trackEvent(`MCP Snippet Copied (${current.value.id})`)
  } catch {}
  clearTimeout(copyTimer)
  copyTimer = setTimeout(() => { copied.value = false }, 2000)
}

onBeforeUnmount(() => clearTimeout(copyTimer))
</script>

<style scoped>
/* The header has to hold three tabs and the copy button at any card width —
   a strip that scrolls would hide a client behind the edge. As the card (the
   size container, see ApiAccessCard.vue) narrows, the copy button drops to
   its icon, then the tabs drop their marks. The label stays in the
   accessibility tree either way. */
@container (max-width: 30rem) {
  .mcp-snippet__copy-label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}
@container (max-width: 22rem) {
  .mcp-snippet__glyph { display: none; }
}
</style>
